import React from 'react';
import { Customer, CustomerCategory, ZohoEstimateSummary, ZohoInvoiceSummary } from '../../types';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  getCustomerPaymentBadge,
  getCustomerPaymentBadgeClasses,
} from '../../utils/customerPaymentPolicy';

interface CustomerCardProps {
  customer: Customer;
  category: CustomerCategory | null;
  quotesCount: number;
  quoteValue: number;
  lastQuoteDate?: string | null;
  lastQuoteStatus?: string | null;
  lastQuoteTotal?: number;
  isExpanded: boolean;
  onToggleExpand: (customerId: string) => void;
  onAdjustTerms: (customer: Customer) => void;
  onRemoveClient: (customer: Customer) => Promise<void>;
  onRemoveGroup: (customer: Customer) => Promise<void>;
  onOpenZohoContact: (zohoContactId: string) => void;
  onRefreshZohoCustomer: (customer: Customer) => Promise<void>;
  onRefreshFinance: (customer: Customer) => Promise<void>;
  loadingFinanceId: string | null;
  isPriority?: boolean;
  financeCurrency: string;
  displayOutstanding: number;
  displayInvoicesTotal: number;
  displayInvoicesCount: number;
  displayEstimatesTotal: number;
  displayEstimatesCount: number;
  activeFinance?: { invoices?: ZohoInvoiceSummary; estimates?: ZohoEstimateSummary } | null;
  lastInvoice?: { date?: string; status?: string } | null;
  lastEstimate?: { date?: string; status?: string } | null;
  lastActivityDate?: string | null;
  lastActivityLabel: string;
  hasMaosActivity: boolean;
  lastZohoEstimate?: { number?: string; date?: string } | null;
  onLinkZohoContact: (customer: Customer) => void;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  category,
  quotesCount,
  quoteValue,
  lastQuoteDate,
  lastQuoteStatus,
  lastQuoteTotal,
  isExpanded,
  onToggleExpand,
  onAdjustTerms,
  onRemoveClient,
  onRemoveGroup,
  onOpenZohoContact,
  onRefreshZohoCustomer,
  onRefreshFinance,
  loadingFinanceId,
  isPriority,
  financeCurrency,
  displayOutstanding,
  displayInvoicesTotal,
  displayInvoicesCount,
  displayEstimatesTotal,
  displayEstimatesCount,
  activeFinance,
  lastInvoice,
  lastEstimate,
  lastActivityDate,
  lastActivityLabel,
  hasMaosActivity,
  lastZohoEstimate,
  onLinkZohoContact,
}) => {
  const effectiveCharter = customer.charterDiscount > 0 ? customer.charterDiscount : (category?.defaultCharterDiscount || 0);
  const effectiveSchedule = customer.scheduleDiscount > 0 ? customer.scheduleDiscount : (category?.defaultScheduleDiscount || 0);
  const isZohoLinked = !!customer.zohoContactId;
  const paymentBadge = getCustomerPaymentBadge(customer);
  const zohoTermsLabel = customer.zohoPaymentTermsLabel?.trim();
  const zohoTermsDays =
    typeof customer.zohoPaymentTermsDays === 'number' && Number.isFinite(customer.zohoPaymentTermsDays)
      ? customer.zohoPaymentTermsDays
      : null;
  const zohoTermsSummary = zohoTermsLabel || (zohoTermsDays !== null ? `Net ${zohoTermsDays}` : null);

  return (
    <div
      className={`bg-white rounded-[2.5rem] p-8 shadow-sm hover:shadow-xl transition-all group overflow-hidden relative ${
        isPriority ? 'border border-emerald-100' : 'border border-slate-100'
      }`}
    >
      {isPriority && (
        <div className="absolute top-0 right-0 text-[9px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 px-3 py-1 rounded-bl-2xl">
          Priority
        </div>
      )}
      {!isPriority && (
        <div
          className={`absolute top-0 right-0 w-32 h-32 -translate-y-16 translate-x-16 rounded-full opacity-5 ${
            customer.source === 'ZOHO' ? 'bg-indigo-600' : 'bg-slate-400'
          }`}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center font-black text-indigo-600 shadow-sm border border-slate-100">
              {customer.name.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h4 className="font-black text-slate-800 leading-none mb-1">{customer.name}</h4>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                {customer.source || 'LOCAL'} PARTNER
              </span>
              {category && (
                <span className="block text-[9px] font-black text-indigo-600 uppercase tracking-widest mt-1">{category.name}</span>
              )}
            </div>
          </div>
          <div className="pt-2">
            <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">Legal Identity</p>
            <p className="text-xs font-bold text-slate-600">{customer.legalName || 'N/A'}</p>
            {customer.tin && <p className="text-[9px] font-mono text-slate-400 mt-1">TIN: {customer.tin}</p>}
            {customer.email && <p className="text-[9px] font-mono text-slate-400 mt-1">{customer.email}</p>}
          </div>
        </div>

        <div className="lg:col-span-1 border-x border-slate-50 px-8">
          <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-6">Commercial Gateway</p>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">Charter Rebate</span>
              <span className="text-sm font-black text-indigo-600">{effectiveCharter}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">Schedule Rebate</span>
              <span className="text-sm font-black text-indigo-600">{effectiveSchedule}%</span>
            </div>
            {(customer.charterDiscount === 0 || customer.scheduleDiscount === 0) && category && (
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Using Group Defaults: {category.name}</p>
            )}
            {category && (
              <div className="pt-4 border-t border-slate-50">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">Group</span>
                  <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{category.name}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-6">Operator Snapshot</p>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">Outstanding</span>
              <span className="text-sm font-black text-rose-600">
                {formatCurrency(Number(displayOutstanding || 0), financeCurrency)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">Credit Limit</span>
              <span className="text-xs font-black text-slate-800">
                {formatCurrency(Number(customer.creditLimit || 0), financeCurrency)}
              </span>
            </div>
            {zohoTermsSummary && (
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">Zoho Terms</span>
                <span className="text-[10px] font-black text-slate-700">{zohoTermsSummary}</span>
              </div>
            )}
            <div className="pt-3 border-t border-slate-50">
              <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-2">Zoho Finance</p>
              {activeFinance ? (
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Invoices</p>
                    <p className="mt-1 text-sm font-black text-slate-700">{displayInvoicesCount ?? 0}</p>
                    <p className="text-[10px] text-slate-500">Total</p>
                    <p className="text-[10px] font-black text-slate-700">
                      {formatCurrency(Number(displayInvoicesTotal || 0), financeCurrency)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 px-3 py-2">
                    <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">Estimates</p>
                    <p className="mt-1 text-sm font-black text-indigo-600">{displayEstimatesCount ?? 0}</p>
                    <p className="text-[10px] text-slate-500">Total</p>
                    <p className="text-[10px] font-black text-indigo-700">
                      {formatCurrency(Number(displayEstimatesTotal || 0), financeCurrency)}
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => onRefreshFinance(customer)}
                  className="text-[10px] font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-widest"
                >
                  {loadingFinanceId === customer.id ? 'Loading...' : 'Load Zoho Finance'}
                </button>
              )}
            </div>
            <div className="pt-3 border-t border-slate-50">
              <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-2">Recent Activity</p>
              {lastActivityDate ? (
                <p className="text-xs font-bold text-slate-700">
                  {new Date(lastActivityDate).toLocaleDateString()}{' '}
                  <span className="text-slate-400 font-black">{lastActivityLabel}</span>
                </p>
              ) : (
                <p className="text-xs text-slate-400">No recent activity</p>
              )}
              {lastEstimate?.date && (
                <p className="text-[10px] text-indigo-600 mt-1">
                  Last Estimate: {new Date(lastEstimate.date).toLocaleDateString()} {lastEstimate.status ? `(${lastEstimate.status})` : ''}
                </p>
              )}
              {lastZohoEstimate?.number && (
                <p className="text-[10px] text-emerald-600 mt-1">
                  Zoho Estimate: {lastZohoEstimate.number}
                  {lastZohoEstimate.date ? ` · ${new Date(lastZohoEstimate.date).toLocaleDateString()}` : ''}
                </p>
              )}
              {lastQuoteDate && (
                <p className="text-[10px] text-slate-600 mt-1">
                  Last Quote: {new Date(lastQuoteDate).toLocaleDateString()} {lastQuoteStatus ? `(${lastQuoteStatus})` : ''} ·{' '}
                  {formatCurrency(Number(lastQuoteTotal || 0), financeCurrency)}
                </p>
              )}
              {hasMaosActivity && (
                <div className="mt-2 text-xs text-slate-500">
                  ME-OS total: {formatCurrency(Number(quoteValue || 0), financeCurrency)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1 flex flex-col justify-end gap-3 text-right">
          <div className="flex flex-wrap gap-2 justify-end">
            {lastInvoice?.date && (
              <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-50 text-emerald-600">
                Last Inv {new Date(lastInvoice.date).toLocaleDateString()}
              </span>
            )}
            {lastQuoteDate && (
              <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-slate-50 text-slate-500">
                Last Quote {new Date(lastQuoteDate).toLocaleDateString()}
              </span>
            )}
            <span
              className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${
                isZohoLinked ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-700'
              }`}
            >
              {isZohoLinked ? 'Identity Linked' : 'Needs Link'}
            </span>
          </div>
          <span
            className={`text-[10px] font-black uppercase tracking-widest self-end px-3 py-1 rounded-full ${
              customer.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
            }`}
          >
            {customer.status}
          </span>
          <span
            className={`text-[9px] font-black uppercase tracking-widest self-end px-3 py-1 rounded-full ${getCustomerPaymentBadgeClasses(paymentBadge.tone)}`}
            title={paymentBadge.warningLabel}
          >
            {paymentBadge.label}
          </span>
          {customer.zohoContactId && (
            <button
              onClick={() => onRefreshZohoCustomer(customer)}
              className="text-[10px] font-black uppercase tracking-widest border rounded-xl px-3 py-2 border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              Refresh From Zoho
            </button>
          )}
          {customer.zohoContactId && (
            <button
              onClick={() => onRefreshFinance(customer)}
              className={`text-[10px] font-black uppercase tracking-widest border rounded-xl px-3 py-2 ${
                loadingFinanceId === customer.id
                  ? 'border-slate-200 text-slate-400 bg-slate-50'
                  : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
              }`}
            >
              {loadingFinanceId === customer.id ? 'Refreshing...' : 'Refresh Live'}
            </button>
          )}
          {category && (
            <button
              onClick={() => onRemoveGroup(customer)}
              className="text-[10px] font-black uppercase tracking-widest border rounded-xl px-3 py-2 border-rose-200 text-rose-600 hover:bg-rose-50"
            >
              Remove Group
            </button>
          )}
          <button
            onClick={() => onRemoveClient(customer)}
            className="text-[10px] font-black uppercase tracking-widest border rounded-xl px-3 py-2 border-rose-200 text-rose-600 hover:bg-rose-50"
          >
            Remove Client
          </button>
          <button
            onClick={() => onLinkZohoContact(customer)}
            className={`text-[10px] font-black uppercase tracking-widest border rounded-xl px-3 py-2 ${
              isZohoLinked
                ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                : 'border-amber-200 text-amber-700 hover:bg-amber-50'
            }`}
          >
            {isZohoLinked ? 'Change Zoho Link' : 'Link Zoho Contact'}
          </button>
          {customer.zohoContactId && (
            <button
              onClick={() => onOpenZohoContact(customer.zohoContactId!)}
              className="text-[10px] font-black uppercase tracking-widest border rounded-xl px-3 py-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            >
              Open Zoho Contact
            </button>
          )}
          <button
            onClick={() => onToggleExpand(customer.id)}
            className="text-[10px] font-black uppercase tracking-widest border rounded-xl px-3 py-2 border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            {isExpanded ? 'Hide Details' : 'More Details'}
          </button>
          <button
            onClick={() => onAdjustTerms(customer)}
            className={`text-[10px] font-black uppercase tracking-widest border rounded-xl px-3 py-2 ${
              isPriority ? 'border-indigo-200 text-indigo-700 hover:bg-indigo-50' : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50'
            }`}
          >
            Adjust Commercial Terms
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-6 border-t border-slate-100 pt-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-xs">
          <div className="space-y-2">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact</p>
            <p className="font-bold text-slate-700">{customer.email || 'No email'}</p>
            <p className="font-bold text-slate-700">{customer.phoneNumber || 'No phone'}</p>
            <p className="text-slate-500">{customer.company || customer.legalName || 'No company'}</p>
          </div>
          <div className="space-y-2">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Identifiers</p>
            <p className="font-mono text-slate-600">ID: {customer.id}</p>
            {customer.zohoContactId && <p className="font-mono text-slate-600">Zoho: {customer.zohoContactId}</p>}
            {customer.tin && <p className="font-mono text-slate-600">TIN: {customer.tin}</p>}
          </div>
          <div className="space-y-2">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Activity</p>
            <p className="font-bold text-slate-700">Total Quotes/Missions: {quotesCount}</p>
            <p className="font-bold text-slate-700">Total Value: {formatCurrency(Number(quoteValue || 0), financeCurrency)}</p>
            {lastQuoteDate ? (
              <p className="text-slate-600">
                Last: {new Date(lastQuoteDate).toLocaleDateString()} ({lastQuoteStatus || 'UNKNOWN'})
              </p>
            ) : (
              <p className="text-slate-400">No activity yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomerCard);
